import { formatFinanceDate } from "@/utils/format-finance-date";
import { useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  TableBody,
  TableCell,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Button,
  Label,
  Badge,
} from "@/components/ui";
import { FinanceEntryReportResponse } from "@/types";
import { formatDate, formatValue, isDateValid } from "@/utils";

import { Pagination } from "../../pagination";
import { defaultFinancialAccounts } from "@/helpers";

type BillingPaymentDataTableProps = {
  data: FinanceEntryReportResponse;
  perPage: number;
  handlePerPageChange: (newPerPage: any) => void;
};

type ColunsBillingPaymentProps = {
  id: string;
  account: string;
  patients: string;
  value: string;
  launch: string;
  payment: string;
  maturity: string;
  search_payment_form: string;
  status: string;
};

export function BillingPaymentDataTableReport({
  data: { items, data },
  perPage,
  handlePerPageChange,
}: BillingPaymentDataTableProps) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  function getData() {
    const data = items.map((item) => ({
      id: item.id,
      account: item.account.name,
      patients: item.patient.name,
      value: formatValue(item.value),
      launch: formatDate(item.created_at, false, true),
      payment: item.payday ?? t("LABEL.no paid"),
      maturity: formatFinanceDate(item.date),
      search_payment_form: item.payment_method,
      status: item.status ?? t(`LABEL.open`),
    }));
    return data;
  }

  function isValidDateString(dateStr: string): boolean {
    if (!dateStr || typeof dateStr !== "string") return false;

    const parsedDate = new Date(dateStr);

    return !isNaN(parsedDate.getTime());
  }



  const columns: ColumnDef<ColunsBillingPaymentProps>[] = [
    {
      accessorKey: "patients",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.patient")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("patients")}</div>
      ),
    },
    {
      accessorKey: "account",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.account")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const account = row.getValue("account") as string | null | undefined;
        if (!account) {
          return <div className="text-app-fg-muted">N/A</div>;
        }
        return (
          <div className="capitalize">
            {defaultFinancialAccounts.includes(account)
              ? t(account.toLowerCase())
              : account}
          </div>
        )
      },
    },
    {
      accessorKey: "payment",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.payment")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const payment = row.getValue("payment") as string;
        return <div>{isValidDateString(payment) ? t(`LABEL.paid`) : t(payment)}</div>;
      },
    },
    {
      accessorKey: "value",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.value")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const value = row.getValue("value") as string;
        return <div>{value}</div>;
      },
    },
    {
      accessorKey: "launch",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.launch")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("launch")}</div>
      ),
    },
    {
      accessorKey: "maturity",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.maturity")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("maturity")}</div>
      ),
    },
    {
      accessorKey: "search_payment_form",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.payment method")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        // Open entries carry no payment method yet; the report must not crash on them.
        const paymentMethod = row.getValue("search_payment_form") as string | null | undefined;
        if (!paymentMethod) {
          return <div className="text-app-fg-muted">N/A</div>;
        }
        return <div>{paymentMethod === "CreditCard" ? "Cartão de crédito" : paymentMethod === "DebitCard" ? "Cartão de débito" : t(`LABEL.${paymentMethod.toLowerCase()}`)}</div>;
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Status
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        return (
          <div>
            <span> {isDateValid(status) && <> {t("LABEL.paid")} - {status}</>} </span>
            <span>
              {!isDateValid(status) && status && (
                status === "delayed" ? (
                  <Badge className="bg-red-100 text-red-800 hover:text-red-800 hover:bg-red-200">
                    {t(status)}
                  </Badge>
                ) : (
                  <Badge className="bg-yellow-100 text-yellow-800 hover:text-yellow-800 hover:bg-yellow-200">
                    {t(status)}
                  </Badge>
                )
              )}
            </span>
          </div>
        );
      },
    },
  ];


  const dataMemo = useMemo(() => getData(), [items]);

  const columnsMemo: any = useMemo(() => columns, []);

  const table = useReactTable({
    data: dataMemo,
    columns: columnsMemo,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      <div className="flex justify-between items-center py-4">
        <div className="flex items-center gap-x-3 max-w-sm">
          <Label>{t("LABEL.show")}</Label>
          <select
            onChange={(e) => handlePerPageChange(Number(e.target.value))}
            value={perPage}
            className="py-1 px-2 text-sm rounded-lg border border-zinc-300 bg-zinc-50 focus:border-green-500 outline-none"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <Label>itens</Label>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="ml-auto bg-zinc-50 outline-none"
            >
              {t("LABEL.columns")} <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    // onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    onCheckedChange={(value) => {
                      setColumnVisibility((prev) => ({
                        ...prev,
                        [column.id]: !!value,
                      }));
                    }}
                  >
                    {t(`LABEL.${column.id}`)}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isExcludedColumn = cell.column.id === "select";
                    return (
                      <>
                        {!isExcludedColumn ? (
                          <TableCell key={cell.id}>
                            <div className="cursor-pointer p-2">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </div>
                          </TableCell>
                        ) : (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        )}
                      </>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {t("no results")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination data={data} />
    </div>
  );
}
